/**
 * Atlassian Integration Component
 *
 * Provides UI for managing Jira and Confluence integrations:
 * - Connection management (OAuth/PAT)
 * - Confluence space mappings and document import
 * - Jira project mappings and task sync
 */

import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { useToast } from './Toast'
import { useConfirm } from './ConfirmModal'
import {
  Plus,
  Trash2,
  RefreshCw,
  Settings,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Link2,
  FileText,
  FolderOpen,
  Book,
  Layers,
  Play,
  Pause,
  Upload,
  Download,
} from 'lucide-react'

// ============= Types =============

interface AtlassianConnection {
  id: string
  name: string
  type: 'cloud' | 'server' | 'datacenter'
  base_url: string
  status: 'active' | 'inactive' | 'error'
  auth_type: 'oauth' | 'pat' | 'basic'
  created_at: number
  last_verified_at: number | null
}

interface ConfluenceSpaceMapping {
  id: string
  space_key: string
  space_name: string
  document_type: string
  target_framework_id: string | null
  auto_import: boolean
  import_attachments: boolean
  last_import_at: number | null
  page_count: number
}

interface ConfluenceImportJob {
  id: string
  space_key: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'partial'
  pages_total: number
  pages_imported: number
  pages_failed: number
  started_at: number
  completed_at: number | null
}

interface JiraProjectMapping {
  id: string
  project_key: string
  project_name: string
  default_issue_type: string
  sync_enabled: boolean
  sync_comments: boolean
  create_evidence_on_close: boolean
}

// ============= Main Component =============

export default function AtlassianIntegration() {
  // Connections state
  const [connections, setConnections] = useState<AtlassianConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedConnection, setSelectedConnection] = useState<AtlassianConnection | null>(null)
  const [showConnectModal, setShowConnectModal] = useState(false)

  // Confluence state
  const [spaceMappings, setSpaceMappings] = useState<ConfluenceSpaceMapping[]>([])
  const [importJobs, setImportJobs] = useState<ConfluenceImportJob[]>([])
  const [showSpaceModal, setShowSpaceModal] = useState(false)
  const [availableSpaces, setAvailableSpaces] = useState<Array<{ id: string; key: string; name: string }>>([])
  const [loadingSpaces, setLoadingSpaces] = useState(false)

  // Jira state
  const [projectMappings, setProjectMappings] = useState<JiraProjectMapping[]>([])
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [availableProjects, setAvailableProjects] = useState<Array<{ id: string; key: string; name: string }>>([])
  const [loadingProjects, setLoadingProjects] = useState(false)

  // Active sub-section
  const [section, setSection] = useState<'connections' | 'confluence' | 'jira'>('connections')

  const toast = useToast()
  const confirmDialog = useConfirm()

  // ============= Data Loading =============

  const loadConnections = useCallback(async () => {
    try {
      const response = await api.get('/api/atlassian/connections')
      if (response.success) {
        setConnections(response.connections || [])
        if (response.connections?.length > 0 && !selectedConnection) {
          setSelectedConnection(response.connections[0])
        }
      }
    } catch (error) {
      console.error('Failed to load Atlassian connections:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedConnection])

  const loadSpaceMappings = useCallback(async (connectionId: string) => {
    try {
      const response = await api.get(`/atlassian/confluence/${connectionId}/mappings`)
      if (response.success) {
        setSpaceMappings(response.mappings || [])
      }
    } catch (error) {
      console.error('Failed to load space mappings:', error)
    }
  }, [])

  const loadImportJobs = useCallback(async (connectionId: string) => {
    try {
      const response = await api.get(`/atlassian/confluence/${connectionId}/import-jobs`)
      if (response.success) {
        setImportJobs(response.jobs || [])
      }
    } catch (error) {
      console.error('Failed to load import jobs:', error)
    }
  }, [])

  const loadProjectMappings = useCallback(async (connectionId: string) => {
    try {
      const response = await api.get(`/atlassian/jira/${connectionId}/mappings`)
      if (response.success) {
        setProjectMappings(response.mappings || [])
      }
    } catch (error) {
      console.error('Failed to load project mappings:', error)
    }
  }, [])

  useEffect(() => {
    loadConnections()
  }, [loadConnections])

  useEffect(() => {
    if (selectedConnection) {
      loadSpaceMappings(selectedConnection.id)
      loadImportJobs(selectedConnection.id)
      loadProjectMappings(selectedConnection.id)
    }
  }, [selectedConnection, loadSpaceMappings, loadImportJobs, loadProjectMappings])

  // ============= Connection Handlers =============

  const handleConnectOAuth = async () => {
    // Open OAuth popup - server has the client ID configured
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      `/api/atlassian/oauth/authorize`,
      'atlassian-oauth',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    )

    // Listen for OAuth completion
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'atlassian-oauth-success') {
        popup?.close()
        window.removeEventListener('message', handleMessage)
        setShowConnectModal(false)
        loadConnections()
        toast.success('Atlassian connection successful!')
      } else if (event.data?.type === 'atlassian-oauth-error') {
        popup?.close()
        window.removeEventListener('message', handleMessage)
        toast.error(event.data.error || 'OAuth connection failed')
      }
    }

    window.addEventListener('message', handleMessage)
  }

  const handleDeleteConnection = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Delete Connection',
      message: 'Are you sure you want to delete this Atlassian connection? All associated mappings will also be deleted.',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed) return

    try {
      await api.delete(`/atlassian/connections/${id}`)
      toast.success('Connection deleted')
      await loadConnections()
      if (selectedConnection?.id === id) {
        setSelectedConnection(null)
      }
    } catch (error) {
      toast.error('Failed to delete connection')
    }
  }

  const handleTestConnection = async (id: string) => {
    try {
      const response = await api.post(`/atlassian/connections/${id}/test`)
      if (response.success) {
        toast.success('Connection is working!')
        await loadConnections()
      } else {
        toast.error(response.error || 'Connection test failed')
      }
    } catch (error) {
      toast.error('Connection test failed')
    }
  }

  // ============= Confluence Handlers =============

  const handleLoadSpaces = async () => {
    if (!selectedConnection) return
    setLoadingSpaces(true)
    try {
      const response = await api.get(`/atlassian/confluence/${selectedConnection.id}/spaces`)
      if (response.success) {
        setAvailableSpaces(response.spaces || [])
        setShowSpaceModal(true)
      }
    } catch (error) {
      toast.error('Failed to load Confluence spaces')
    } finally {
      setLoadingSpaces(false)
    }
  }

  const handleAddSpaceMapping = async (space: { id: string; key: string; name: string }) => {
    if (!selectedConnection) return
    try {
      await api.post(`/atlassian/confluence/${selectedConnection.id}/mappings`, {
        space_id: space.id,
        space_key: space.key,
        space_name: space.name,
        document_type: 'policy',
        auto_import: false,
        import_attachments: false,
      })
      toast.success(`Added mapping for space ${space.key}`)
      setShowSpaceModal(false)
      await loadSpaceMappings(selectedConnection.id)
    } catch (error) {
      toast.error('Failed to add space mapping')
    }
  }

  const handleImportSpace = async (mappingId: string) => {
    if (!selectedConnection) return
    try {
      const response = await api.post(`/atlassian/confluence/${selectedConnection.id}/import`, {
        mapping_id: mappingId,
      })
      if (response.success) {
        toast.success('Import job started')
        await loadImportJobs(selectedConnection.id)
      }
    } catch (error) {
      toast.error('Failed to start import')
    }
  }

  const handleDeleteSpaceMapping = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Delete Space Mapping',
      message: 'Are you sure? This will not delete already imported documents.',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed || !selectedConnection) return

    try {
      await api.delete(`/atlassian/confluence/${selectedConnection.id}/mappings/${id}`)
      toast.success('Space mapping deleted')
      await loadSpaceMappings(selectedConnection.id)
    } catch (error) {
      toast.error('Failed to delete mapping')
    }
  }

  // ============= Jira Handlers =============

  const handleLoadProjects = async () => {
    if (!selectedConnection) return
    setLoadingProjects(true)
    try {
      const response = await api.get(`/atlassian/jira/${selectedConnection.id}/projects`)
      if (response.success) {
        setAvailableProjects(response.projects || [])
        setShowProjectModal(true)
      }
    } catch (error) {
      toast.error('Failed to load Jira projects')
    } finally {
      setLoadingProjects(false)
    }
  }

  const handleAddProjectMapping = async (project: { id: string; key: string; name: string }) => {
    if (!selectedConnection) return
    try {
      await api.post(`/atlassian/jira/${selectedConnection.id}/mappings`, {
        project_key: project.key,
        project_name: project.name,
        project_id: project.id,
        default_issue_type: 'Task',
        sync_enabled: true,
        sync_comments: true,
        create_evidence_on_close: true,
      })
      toast.success(`Added mapping for project ${project.key}`)
      setShowProjectModal(false)
      await loadProjectMappings(selectedConnection.id)
    } catch (error) {
      toast.error('Failed to add project mapping')
    }
  }

  const handleDeleteProjectMapping = async (id: string) => {
    const confirmed = await confirmDialog({
      title: 'Delete Project Mapping',
      message: 'Are you sure? Existing task syncs will be preserved.',
      type: 'danger',
      confirmText: 'Delete',
    })
    if (!confirmed || !selectedConnection) return

    try {
      await api.delete(`/atlassian/jira/${selectedConnection.id}/mappings/${id}`)
      toast.success('Project mapping deleted')
      await loadProjectMappings(selectedConnection.id)
    } catch (error) {
      toast.error('Failed to delete mapping')
    }
  }

  const handleToggleProjectSync = async (mapping: JiraProjectMapping) => {
    if (!selectedConnection) return
    try {
      await api.patch(`/atlassian/jira/${selectedConnection.id}/mappings/${mapping.id}`, {
        sync_enabled: !mapping.sync_enabled,
      })
      await loadProjectMappings(selectedConnection.id)
    } catch (error) {
      toast.error('Failed to update mapping')
    }
  }

  // ============= Utility Functions =============

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString()
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'error':
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'running':
      case 'pending':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  // ============= Render =============

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            Atlassian Integration
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Connect Jira and Confluence to sync tasks and import documents
          </p>
        </div>
        <button
          onClick={() => setShowConnectModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-5 w-5" />
          Connect Atlassian
        </button>
      </div>

      {/* No connections state */}
      {connections.length === 0 ? (
        <div className="space-y-6">
          {/* Empty State Card */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <Link2 className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Connect Atlassian</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Integrate with Jira and Confluence to streamline your GRC workflows
              </p>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 max-w-2xl mx-auto text-left">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-900">Jira Integration</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Create Jira tickets from GRC tasks</li>
                    <li>• Two-way sync for status updates</li>
                    <li>• Sync comments between systems</li>
                    <li>• Auto-create evidence on ticket close</li>
                  </ul>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Book className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-gray-900">Confluence Integration</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Import pages as policies/procedures</li>
                    <li>• Map spaces to document types</li>
                    <li>• Import attachments as evidence</li>
                    <li>• Schedule recurring imports</li>
                  </ul>
                </div>
              </div>

              <button
                onClick={() => setShowConnectModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <Plus className="h-5 w-5" />
                Connect Atlassian Account
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Connection Sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-medium text-gray-900">Connections</h3>
              </div>
              <div className="divide-y">
                {connections.map((conn) => (
                  <button
                    key={conn.id}
                    onClick={() => setSelectedConnection(conn)}
                    className={`w-full p-4 text-left hover:bg-gray-50 ${
                      selectedConnection?.id === conn.id ? 'bg-blue-50 border-l-2 border-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{conn.name}</span>
                      {getStatusIcon(conn.status)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {conn.type} - {conn.auth_type}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            {selectedConnection ? (
              <div className="space-y-6">
                {/* Connection Details */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(selectedConnection.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedConnection.name}</h3>
                        <p className="text-sm text-gray-500">{selectedConnection.base_url}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleTestConnection(selectedConnection.id)}
                        className="flex items-center gap-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Test
                      </button>
                      <button
                        onClick={() => handleDeleteConnection(selectedConnection.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 text-gray-900 capitalize">{selectedConnection.type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Auth:</span>
                      <span className="ml-2 text-gray-900 uppercase">{selectedConnection.auth_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Last Verified:</span>
                      <span className="ml-2 text-gray-900">{formatDate(selectedConnection.last_verified_at)}</span>
                    </div>
                  </div>
                </div>

                {/* Section Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex gap-6">
                    <button
                      onClick={() => setSection('confluence')}
                      className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        section === 'confluence'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Book className="h-4 w-4" />
                      Confluence
                      {spaceMappings.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 rounded-full">
                          {spaceMappings.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setSection('jira')}
                      className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        section === 'jira'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Layers className="h-4 w-4" />
                      Jira
                      {projectMappings.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 rounded-full">
                          {projectMappings.length}
                        </span>
                      )}
                    </button>
                  </nav>
                </div>

                {/* Confluence Section */}
                {section === 'confluence' && (
                  <div className="space-y-4">
                    {/* Space Mappings */}
                    <div className="bg-white rounded-xl border border-gray-200">
                      <div className="p-4 border-b flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">Confluence Spaces</h4>
                        <button
                          onClick={handleLoadSpaces}
                          disabled={loadingSpaces}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loadingSpaces ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          Add Space
                        </button>
                      </div>
                      <div className="divide-y">
                        {spaceMappings.length === 0 ? (
                          <div className="p-8 text-center text-gray-500">
                            <FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No Confluence spaces mapped</p>
                            <p className="text-xs mt-1">Add a space to start importing documents</p>
                          </div>
                        ) : (
                          spaceMappings.map((mapping) => (
                            <div key={mapping.id} className="p-4 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{mapping.space_name}</span>
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {mapping.space_key}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  {mapping.page_count} pages - Type: {mapping.document_type} -
                                  Last import: {formatDate(mapping.last_import_at)}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleImportSpace(mapping.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                                >
                                  <Download className="h-4 w-4" />
                                  Import
                                </button>
                                <button
                                  onClick={() => handleDeleteSpaceMapping(mapping.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Import Jobs */}
                    {importJobs.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200">
                        <div className="p-4 border-b">
                          <h4 className="font-medium text-gray-900">Import History</h4>
                        </div>
                        <div className="divide-y">
                          {importJobs.slice(0, 5).map((job) => (
                            <div key={job.id} className="p-4 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {getStatusIcon(job.status)}
                                <div>
                                  <span className="font-medium text-gray-900">{job.space_key}</span>
                                  <div className="text-xs text-gray-500">
                                    {job.pages_imported}/{job.pages_total} pages -
                                    {job.pages_failed > 0 && <span className="text-red-600"> {job.pages_failed} failed</span>}
                                  </div>
                                </div>
                              </div>
                              <span className="text-xs text-gray-500">{formatDate(job.started_at)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Jira Section */}
                {section === 'jira' && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-gray-200">
                      <div className="p-4 border-b flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">Jira Projects</h4>
                        <button
                          onClick={handleLoadProjects}
                          disabled={loadingProjects}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loadingProjects ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                          Add Project
                        </button>
                      </div>
                      <div className="divide-y">
                        {projectMappings.length === 0 ? (
                          <div className="p-8 text-center text-gray-500">
                            <Layers className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-sm">No Jira projects mapped</p>
                            <p className="text-xs mt-1">Add a project to enable task syncing</p>
                          </div>
                        ) : (
                          projectMappings.map((mapping) => (
                            <div key={mapping.id} className="p-4 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{mapping.project_name}</span>
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                    {mapping.project_key}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                  <span>Issue Type: {mapping.default_issue_type}</span>
                                  {mapping.sync_comments && <span>Comments synced</span>}
                                  {mapping.create_evidence_on_close && <span>Auto-evidence on close</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleToggleProjectSync(mapping)}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg ${
                                    mapping.sync_enabled
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {mapping.sync_enabled ? (
                                    <>
                                      <Play className="h-3 w-3" /> Active
                                    </>
                                  ) : (
                                    <>
                                      <Pause className="h-3 w-3" /> Paused
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleDeleteProjectMapping(mapping.id)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Settings className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a connection to manage</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Space Selection Modal */}
      {showSpaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[70vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Select Confluence Space</h3>
              <button onClick={() => setShowSpaceModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="divide-y">
              {availableSpaces.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No spaces available</div>
              ) : (
                availableSpaces.map((space) => (
                  <button
                    key={space.id}
                    onClick={() => handleAddSpaceMapping(space)}
                    className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{space.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{space.key}</span>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Project Selection Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[70vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Select Jira Project</h3>
              <button onClick={() => setShowProjectModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="divide-y">
              {availableProjects.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No projects available</div>
              ) : (
                availableProjects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => handleAddProjectMapping(project)}
                    className="w-full p-4 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div>
                      <span className="font-medium text-gray-900">{project.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{project.key}</span>
                    </div>
                    <Plus className="h-4 w-4 text-gray-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connect Atlassian Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowConnectModal(false)}
            />
            <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Link2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Connect Atlassian</h3>
                      <p className="text-blue-100 text-sm">Jira & Confluence Integration</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowConnectModal(false)}
                    className="text-white/80 hover:text-white"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5">
                <p className="text-gray-600 mb-6">
                  Connect your Atlassian account to enable Jira and Confluence integration. You'll be redirected to Atlassian to authorize access.
                </p>

                {/* What you're authorizing */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">This integration will be able to:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-blue-100 rounded">
                        <Layers className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Jira</p>
                        <p className="text-xs text-gray-500">Read and create issues, sync task status and comments</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 bg-blue-100 rounded">
                        <Book className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Confluence</p>
                        <p className="text-xs text-gray-500">Read spaces and pages to import as policies and procedures</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What happens next */}
                <div className="flex items-start gap-2 text-sm text-gray-500">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    A popup will open for you to sign in to Atlassian and grant permissions. Make sure popups are enabled for this site.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowConnectModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectOAuth}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Connect with Atlassian
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
